package com.example.readingnotes.presentation.stripeconnect

import com.example.readingnotes.domain.usecase.GetStripeConnectOnboardingLinkUseCase
import com.example.readingnotes.domain.usecase.GetStripeConnectStatusUseCase
import com.example.readingnotes.domain.usecase.HandleStripeConnectCallbackUseCase
import com.example.readingnotes.domain.usecase.RefreshStripeConnectStatusUseCase
import com.example.readingnotes.platform.BrowserOpener
import com.example.readingnotes.util.ErrorMessageProvider
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class StripeConnectViewModel(
    private val getStripeConnectStatusUseCase: GetStripeConnectStatusUseCase,
    private val getStripeConnectOnboardingLinkUseCase: GetStripeConnectOnboardingLinkUseCase,
    private val handleStripeConnectCallbackUseCase: HandleStripeConnectCallbackUseCase,
    private val refreshStripeConnectStatusUseCase: RefreshStripeConnectStatusUseCase,
    private val viewModelScope: CoroutineScope,
) {
    private val _state = MutableStateFlow(StripeConnectState())
    val state: StateFlow<StripeConnectState> = _state.asStateFlow()

    fun onEvent(event: StripeConnectEvent) {
        when (event) {
            is StripeConnectEvent.OnAppear -> loadStatus()
            is StripeConnectEvent.OnStartOnboarding -> startOnboarding()
            is StripeConnectEvent.OnRefresh -> refreshStatus()
            is StripeConnectEvent.OnCallback -> handleCallback(event.code)
            is StripeConnectEvent.OnClearError -> {
                _state.update { it.copy(errorMessage = null) }
            }
            is StripeConnectEvent.OnOpenDashboard -> openStripeDashboard()
        }
    }

    private fun loadStatus() {
        viewModelScope.launch {
            _state.update { it.copy(isLoading = true, errorMessage = null) }

            getStripeConnectStatusUseCase()
                .onSuccess { status ->
                    _state.update {
                        it.copy(
                            isLoading = false,
                            accountStatus = status,
                            errorMessage = null,
                        )
                    }
                }.onFailure { error ->
                    _state.update {
                        it.copy(
                            isLoading = false,
                            errorMessage = ErrorMessageProvider.getLocalizedMessage(error, "ステータスの読み込みに失敗しました"),
                        )
                    }
                }
        }
    }

    private fun startOnboarding() {
        viewModelScope.launch {
            _state.update {
                it.copy(isGeneratingOnboardingLink = true, errorMessage = null, onboardingUrl = null)
            }

            // Generate redirect URL based on platform
            val redirectUrl = "readingnotes://stripe-connect/callback"
            val refreshUrl = "readingnotes://settings/stripe-connect"

            getStripeConnectOnboardingLinkUseCase(redirectUrl, refreshUrl)
                .onSuccess { link ->
                    _state.update {
                        it.copy(
                            isGeneratingOnboardingLink = false,
                            onboardingUrl = link.url,
                        )
                    }
                    // Open the URL in browser
                    BrowserOpener.openUrl(link.url)
                }.onFailure { error ->
                    _state.update {
                        it.copy(
                            isGeneratingOnboardingLink = false,
                            errorMessage =
                                ErrorMessageProvider.getLocalizedMessage(
                                    error,
                                    "オンボーディングリンクの作成に失敗しました",
                                ),
                        )
                    }
                }
        }
    }

    private fun refreshStatus() {
        viewModelScope.launch {
            _state.update { it.copy(isRefreshing = true, errorMessage = null) }

            refreshStripeConnectStatusUseCase()
                .onSuccess { status ->
                    _state.update {
                        it.copy(
                            isRefreshing = false,
                            accountStatus = status,
                            errorMessage = null,
                        )
                    }
                }.onFailure { error ->
                    _state.update {
                        it.copy(
                            isRefreshing = false,
                            errorMessage = ErrorMessageProvider.getLocalizedMessage(error, "ステータスの更新に失敗しました"),
                        )
                    }
                }
        }
    }

    private fun handleCallback(code: String) {
        viewModelScope.launch {
            _state.update { it.copy(isLoading = true, errorMessage = null) }

            handleStripeConnectCallbackUseCase(code)
                .onSuccess { result ->
                    _state.update {
                        it.copy(
                            isLoading = false,
                            accountStatus = result.status,
                            onboardingUrl = null,
                            errorMessage = null,
                        )
                    }
                }.onFailure { error ->
                    _state.update {
                        it.copy(
                            isLoading = false,
                            errorMessage = ErrorMessageProvider.getLocalizedMessage(error, "コールバック処理に失敗しました"),
                        )
                    }
                }
        }
    }

    private fun openStripeDashboard() {
        val status = _state.value.accountStatus
        if (status?.onboardingCompleted == true) {
            viewModelScope.launch {
                // Open Stripe dashboard in browser
                val dashboardUrl = "https://dashboard.stripe.com/${status.country?.lowercase() ?: "test"}"
                BrowserOpener.openUrl(dashboardUrl)
            }
        }
    }
}
